'use client';

import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useRouter } from 'next/navigation';
import { useSession } from 'next-auth/react';
import { fetchActivePromotions, isPromotionValid } from '@/services/api';
import { getPropertyCode } from '@/utils/propertyManager';
import { usePromo } from '@/contexts/PromoContext';
import { isValidDateString } from '@/utils/dateUtils';

const PromoBannerSection = () => {
  const router = useRouter();
  const { data: session, status } = useSession();
  const isAuthenticated = status === 'authenticated' || !!session?.user?.email;
  const { applyPromo, activePromo, isPromoMode, clearPromo } = usePromo();
  const [promotions, setPromotions] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showLoginPrompt, setShowLoginPrompt] = useState(false);
  const [showUsageExceededPopup, setShowUsageExceededPopup] = useState(false);
  const [usageExceededMessage, setUsageExceededMessage] = useState('');
  const [selectedPromo, setSelectedPromo] = useState(null);
  const carouselRef = useRef(null);
  const touchStartX = useRef(0);
  const touchEndX = useRef(0);
  const intervalRef = useRef(null);

  const clearCarouselInterval = useCallback(() => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
  }, []);

  const startCarouselInterval = useCallback(() => {
    clearCarouselInterval();
    intervalRef.current = setInterval(() => {
      setCurrentIndex((prev) => {
        return (prev + 1) % promotions.length;
      });
    }, 4000);
  }, [promotions.length, clearCarouselInterval]);

  // Freeze carousel when any popup is open (must be after clearCarouselInterval/startCarouselInterval definitions)
  const isAnyPopupOpen = showLoginPrompt || showUsageExceededPopup;
  useEffect(() => {
    if (isAnyPopupOpen) {
      clearCarouselInterval();
    } else if (promotions.length > 1) {
      startCarouselInterval();
    }
    return () => clearCarouselInterval();
  }, [isAnyPopupOpen, promotions.length, clearCarouselInterval, startCarouselInterval]);

  useEffect(() => {
    const loadPromotions = async () => {
      try {
        setIsLoading(true);
        const propertyCode = getPropertyCode();
        const result = await fetchActivePromotions(propertyCode);
        
        if (result.status === 'success' && Array.isArray(result.data)) {
          const validPromotions = result.data.filter(isPromotionValid);
          console.log('[PromoBannerSection] loaded', validPromotions.length, 'promos after filter');
          setPromotions(validPromotions);
          setCurrentIndex(0);
        } else {
          setError(result.message || 'Failed to load promotions');
        }
      } catch (err) {
        console.error('Error loading promotions:', err);
        setError(err.message);
      } finally {
        setIsLoading(false);
      }
    };

    loadPromotions();
  }, []);

  useEffect(() => {
    clearCarouselInterval();
    if (promotions.length > 1) {
      startCarouselInterval();
    }
    return () => clearCarouselInterval();
  }, [promotions, startCarouselInterval, clearCarouselInterval]);

  // Touch handlers for mobile swipe
  const handleTouchStart = (e) => {
    touchStartX.current = e.touches[0].clientX;
  };

  const handleTouchMove = (e) => {
    touchEndX.current = e.touches[0].clientX;
  };

  const handleTouchEnd = () => {
    const diff = touchStartX.current - touchEndX.current;
    const threshold = 50;
    
    if (diff > threshold) {
      setCurrentIndex((prev) => Math.min(promotions.length - 1, prev + 1));
    } else if (diff < -threshold) {
      setCurrentIndex((prev) => Math.max(0, prev - 1));
    }
  };

  // Format discount display
  const formatDiscount = (promo) => {
    if (promo.discount_type === 'percentage') {
      return `${promo.discount_value}% OFF`;
    } else if (promo.discount_type === 'fixed_amount') {
      return `Rp ${new Intl.NumberFormat('id-ID').format(promo.discount_value)} OFF`;
    } else if (promo.discount_type === 'free_night') {
      return `${promo.discount_value} FREE NIGHT${promo.discount_value > 1 ? 'S' : ''}`;
    }
    return 'Special Offer';
  };

  // Format date range
  const formatDateRange = (startDate, endDate) => {
    if (!startDate && !endDate) return null;
    
    const options = { day: 'numeric', month: 'short', year: 'numeric' };
    const start = startDate ? new Date(startDate).toLocaleDateString('en-US', options) : null;
    const end = endDate ? new Date(endDate).toLocaleDateString('en-US', options) : null;
    
    if (start && end) {
      return `${start} - ${end}`;
    } else if (start) {
      return `From ${start}`;
    } else if (end) {
      return `Until ${end}`;
    }
    return null;
  };

  // Handle booking with promo - applies promo and filters rooms
  const handleBookWithPromo = async (promo) => {
    // Check if user is authenticated
    if (!isAuthenticated) {
      setSelectedPromo(promo);
      setShowLoginPrompt(true);
      return;
    }
  
    // Apply promo to context (await to ensure it's set before navigation)
    console.log('🎁 Applying promo:', promo);
    const result = await applyPromo(promo);
    console.log('🎁 Promo apply result:', result);
      
    if (!result || !result.success) {
      console.error('❌ Failed to apply promo:', result?.message);
      // Show warning popup if usage_per_guest limit exceeded or stacking rejected
      if (result?.message) {
        setSelectedPromo(promo);
        setUsageExceededMessage(result.message);
        setShowUsageExceededPopup(true);
      }
      return; // Don't navigate if promo failed to apply
    }
    
    // Get current search parameters
    const today = new Date();
    const tomorrow = new Date(today);
    tomorrow.setDate(tomorrow.getDate() + 1);
    
    const formatDate = (date) => {
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      return `${year}-${month}-${day}`;
    };

    // Default dates
    let checkIn = formatDate(today);
    let checkOut = formatDate(tomorrow);

    // Use stay dates from promo if available (guest must stay within this range)
    if (isValidDateString(promo.stay_start)) {
      checkIn = promo.stay_start;
    }
    if (isValidDateString(promo.stay_end)) {
      const stayEnd = new Date(promo.stay_end);
      const checkInDate = new Date(checkIn);
      if (stayEnd > checkInDate) {
        checkOut = formatDate(stayEnd);
      }
    }

    // Apply min nights requirement if exists (extend checkOut if needed)
    if (promo.min_nights) {
      const minCheckOut = new Date(checkIn);
      minCheckOut.setDate(minCheckOut.getDate() + promo.min_nights);
      if (minCheckOut > new Date(checkOut)) {
        checkOut = formatDate(minCheckOut);
      }
    }

    // Build URL with promo code and data
    const propertyCode = getPropertyCode() || 'DEMO';
    const queryParams = new URLSearchParams({
      checkIn,
      checkOut,
      adults: '2',
      children: '0',
      room: '1',
      property: propertyCode,
      promo_code: promo.promo_code,
      promo_data: encodeURIComponent(JSON.stringify({
        id: promo.id,
        promo_name: promo.promo_name,
        promo_code: promo.promo_code,
        discount_type: promo.discount_type,
        discount_value: promo.discount_value,
        max_discount: promo.max_discount,
        min_nights: promo.min_nights,
        min_amount: promo.min_amount,
        room_types: promo.room_types,
        rate_plans: promo.rate_plans,
        room_type_ids: promo.room_type_ids,
        rate_plan_ids: promo.rate_plan_ids
      }))
    });

    // Navigate to room selection with promo active
    router.push(`/?${queryParams.toString()}#room-selection`);
  };

  // Loading skeleton
  if (isLoading) {
    return (
      <div className="mb-8">
        <div className="animate-pulse">
          <div className="h-48 bg-gradient-to-r from-gray-200 to-gray-300 rounded-2xl"></div>
        </div>
      </div>
    );
  }

  // Don't render if no promotions or error
  if (error || promotions.length === 0) {
    return null;
  }

  const currentPromo = promotions[currentIndex];

  return (
    <div className="mb-8">
      {/* Active Promo Banner (when promo is applied) */}
      {isPromoMode && activePromo && (
        <div className="mb-4 bg-gradient-to-r from-green-500 to-emerald-600 rounded-xl p-4 text-white flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-white/20 rounded-full flex items-center justify-center">
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
            </div>
            <div>
              <p className="font-semibold">{activePromo.promo_name}</p>
              <p className="text-sm text-white/80">Promo applied - {formatDiscount(activePromo)}</p>
            </div>
          </div>
          <button 
            onClick={clearPromo}
            className="text-sm bg-white/20 hover:bg-white/30 px-3 py-1 rounded-lg transition-colors"
          >
            Clear Promo
          </button>
        </div>
      )}

      {/* Section Header */}
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-amber-500 to-orange-600 flex items-center justify-center shadow-lg">
            <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v13m0-13V6a2 2 0 112 2h-2zm0 0V5.5A2.5 2.5 0 109.5 8H12zm-7 4h14M5 12a2 2 0 110-4h14a2 2 0 110 4M5 12v7a2 2 0 002 2h10a2 2 0 002-2v-7" />
            </svg>
          </div>
          <div>
            <div className="flex items-center gap-2">
              <h2 className="text-xl font-bold text-gray-900">Special Offers</h2>
              {!isAuthenticated && (
                <span className="px-2 py-0.5 text-xs font-medium bg-amber-100 text-amber-700 border border-amber-200 rounded-full flex items-center gap-1">
                  <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                  </svg>
                  Login Required
                </span>
              )}
            </div>
            <p className="text-sm text-gray-500">
              {isAuthenticated ? 'Tap to activate exclusive discounts' : 'Login required to activate promo codes'}
            </p>
          </div>
        </div>
        
        {/* Carousel Navigation */}
        <div className="flex items-center gap-2">
          <span className="text-xs text-gray-400 bg-gray-100 px-2 py-1 rounded-full font-medium">
            {currentIndex + 1}/{promotions.length}
          </span>
          {promotions.length > 1 && (
            <>
              <button
                onClick={() => {
                  clearCarouselInterval();
                  setCurrentIndex(prev => Math.max(0, prev - 1));
                  startCarouselInterval();
                }}
                disabled={currentIndex === 0}
                className="p-2 rounded-full bg-white shadow-md hover:bg-gray-50 disabled:opacity-30 disabled:cursor-not-allowed transition-all"
              >
                <svg className="w-4 h-4 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                </svg>
              </button>
              <button
                onClick={() => {
                  clearCarouselInterval();
                  setCurrentIndex(prev => Math.min(promotions.length - 1, prev + 1));
                  startCarouselInterval();
                }}
                disabled={currentIndex === promotions.length - 1}
                className="p-2 rounded-full bg-white shadow-md hover:bg-gray-50 disabled:opacity-30 disabled:cursor-not-allowed transition-all"
              >
                <svg className="w-4 h-4 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
              </button>
            </>
          )}
        </div>
      </div>

      {/* Carousel Container */}
      <div 
        ref={carouselRef}
        className="relative overflow-hidden rounded-2xl"
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
      >
        <div 
          className="flex transition-transform duration-500 ease-out will-change-transform"
          style={{ transform: `translateX(-${currentIndex * 100}%)` }}
        >
          {promotions.map((promo, index) => (
            <div 
              key={promo.id || index}
              className="w-full flex-shrink-0"
            >
              {/* Promo Card */}
              <div className={`relative overflow-hidden transition-all duration-300 ${
                activePromo?.id === promo.id 
                  ? 'ring-4 ring-green-400 ring-offset-2' 
                  : ''
              }`} style={{
                background: 'linear-gradient(135deg, #1a1a2e 0%, #16213e 50%, #0f3460 100%)'
              }}>
                {/* Animated Background Pattern */}
                <div className="absolute inset-0">
                  <div className="absolute top-0 right-0 w-96 h-96 bg-gradient-to-br from-amber-500/10 to-orange-600/10 rounded-full transform translate-x-1/3 -translate-y-1/3 animate-pulse"></div>
                  <div className="absolute bottom-0 left-0 w-64 h-64 bg-gradient-to-tr from-purple-500/10 to-pink-600/10 rounded-full transform -translate-x-1/3 translate-y-1/3"></div>
                </div>
                
                {/* Grid Pattern Overlay */}
                <div className="absolute inset-0 opacity-5" style={{
                  backgroundImage: `linear-gradient(rgba(255,255,255,.1) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,.1) 1px, transparent 1px)`,
                  backgroundSize: '20px 20px'
                }}></div>
                
                <div className="relative p-5 md:p-6">
                  <div className="flex flex-col lg:flex-row gap-5 items-stretch">
                    {/* Discount Badge Section */}
                    <div className="flex-shrink-0 flex flex-col items-center justify-center">
                      <div className="relative">
                        <div className="absolute inset-0 bg-gradient-to-br from-amber-400 to-orange-600 blur-xl opacity-60 animate-pulse"></div>
                        <div className="relative bg-gradient-to-br from-amber-400 via-orange-500 to-red-500 text-white px-8 py-5 rounded-2xl shadow-2xl transform hover:scale-105 transition-transform duration-300">
                          <div className="text-center">
                            <div className="text-xs uppercase tracking-wider opacity-80 mb-1">Save up to</div>
                            <div className="text-3xl md:text-4xl font-black tracking-tight">
                              {formatDiscount(promo)}
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                    
                    {/* Promo Details Section */}
                    <div className="flex-1 flex flex-col justify-center">
                      {/* Promo Code Badge */}
                      <div className="inline-flex items-center gap-2 bg-gradient-to-r from-amber-500/20 to-orange-500/20 border border-amber-500/30 text-amber-300 px-4 py-1.5 rounded-full text-sm mb-3 w-fit">
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A1.994 1.994 0 013 12V7a4 4 0 014-4z" />
                        </svg>
                        <span className="font-mono font-bold tracking-wider">{promo.promo_code}</span>
                        <button 
                          onClick={(e) => {
                            e.stopPropagation();
                            navigator.clipboard?.writeText(promo.promo_code);
                          }}
                          className="ml-1 hover:text-white transition-colors"
                          title="Copy code"
                        >
                          <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                          </svg>
                        </button>
                      </div>
                      
                      <h3 className="text-xl md:text-2xl font-bold text-white mb-2">
                        {promo.promo_name}
                      </h3>
                      
                      {promo.description && (
                        <p className="text-gray-300 text-sm mb-4 line-clamp-2">
                          {promo.description}
                        </p>
                      )}
                      
                      {/* Terms & Conditions Grid */}
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-2 mb-4">
                        {/* Booking Period */}
                        {(isValidDateString(promo.booking_start) || isValidDateString(promo.booking_end)) && (
                          <div className="bg-white/5 rounded-lg p-2.5 border border-white/10">
                            <div className="text-xs text-gray-400 mb-1">Book by</div>
                            <div className="text-sm text-white font-medium">
                              {formatDateRange(promo.booking_start, promo.booking_end) || 'Anytime'}
                            </div>
                          </div>
                        )}

                        {/* Stay Period */}
                        {(isValidDateString(promo.stay_start) || isValidDateString(promo.stay_end)) && (
                          <div className="bg-white/5 rounded-lg p-2.5 border border-white/10">
                            <div className="text-xs text-gray-400 mb-1">Stay period</div>
                            <div className="text-sm text-white font-medium">
                              {formatDateRange(promo.stay_start, promo.stay_end) || 'Any date'}
                            </div>
                          </div>
                        )}
                        
                        {/* Min Nights */}
                        {promo.min_nights && (
                          <div className="bg-white/5 rounded-lg p-2.5 border border-white/10">
                            <div className="text-xs text-gray-400 mb-1">Min stay</div>
                            <div className="text-sm text-white font-medium">
                              {promo.min_nights} night{promo.min_nights > 1 ? 's' : ''}
                            </div>
                          </div>
                        )}
                        
                        {/* Max Discount */}
                        {promo.max_discount && promo.discount_type === 'percentage' && (
                          <div className="bg-white/5 rounded-lg p-2.5 border border-white/10">
                            <div className="text-xs text-gray-400 mb-1">Max discount</div>
                            <div className="text-sm text-white font-medium">
                              Rp {new Intl.NumberFormat('id-ID').format(promo.max_discount)}
                            </div>
                          </div>
                        )}
                      </div>
                      
                      {/* Applicable Room Types / Rate Plans */}
                      {(promo.room_types?.length > 0 || promo.rate_plans?.length > 0) && (
                        <div className="mb-4">
                          <div className="text-xs text-gray-400 mb-2">Applicable to:</div>
                          <div className="flex flex-wrap gap-1.5">
                            {promo.room_types?.slice(0, 3).map((room, i) => (
                              <span key={i} className="px-2 py-0.5 bg-blue-500/20 border border-blue-500/30 rounded text-xs text-blue-300">
                                {room}
                              </span>
                            ))}
                            {promo.rate_plans?.slice(0, 2).map((rate, i) => (
                              <span key={i} className="px-2 py-0.5 bg-purple-500/20 border border-purple-500/30 rounded text-xs text-purple-300">
                                {rate}
                              </span>
                            ))}
                            {(promo.room_types?.length > 3 || promo.rate_plans?.length > 2) && (
                              <span className="px-2 py-0.5 bg-gray-500/20 border border-gray-500/30 rounded text-xs text-gray-400">
                                +{Math.max(0, (promo.room_types?.length || 0) - 3) + Math.max(0, (promo.rate_plans?.length || 0) - 2)} more
                              </span>
                            )}
                          </div>
                        </div>
                      )}
                    </div>
                    
                    {/* CTA Button Section */}
                    <div className="flex-shrink-0 flex flex-col items-center gap-2">
                      {!isAuthenticated && (
                        <span className="text-xs text-amber-300 flex items-center gap-1">
                          <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                          </svg>
                          Login required
                        </span>
                      )}
                      <button
                        onClick={() => handleBookWithPromo(promo)}
                        className={`group relative w-full lg:w-auto px-8 py-4 font-bold rounded-xl shadow-xl transition-all duration-300 ${
                          activePromo?.id === promo.id
                            ? 'bg-green-500 text-white hover:bg-green-600'
                            : 'bg-gradient-to-r from-amber-400 via-orange-500 to-red-500 text-white hover:shadow-2xl hover:scale-105'
                        }`}
                      >
                        <span className="flex items-center justify-center gap-2">
                          {activePromo?.id === promo.id ? (
                            <>
                              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                              </svg>
                              <span>Active - View Rooms</span>
                            </>
                          ) : (
                            <>
                              <span>Activate Offer</span>
                              <svg className="w-5 h-5 group-hover:translate-x-1 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" />
                              </svg>
                            </>
                          )}
                        </span>
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
        
        {/* Carousel Indicators */}
        {promotions.length > 1 && (
          <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex gap-2">
            {promotions.map((_, index) => (
              <button
                key={index}
                onClick={() => {
                  clearCarouselInterval();
                  setCurrentIndex(index);
                  startCarouselInterval();
                }}
                className={`h-2 rounded-full transition-all duration-300 ${
                  index === currentIndex 
                    ? 'bg-white w-6' 
                    : 'bg-white/40 hover:bg-white/60 w-2'
                }`}
                aria-label={`Go to promotion ${index + 1}`}
              />
            ))}
          </div>
        )}
      </div>
      
      {/* Login Prompt Modal */}
      {showLoginPrompt && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
          <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full p-6 transform transition-all">
            <div className="text-center">
              <div className="w-16 h-16 mx-auto mb-4 bg-amber-100 rounded-full flex items-center justify-center">
                <svg className="w-8 h-8 text-amber-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                </svg>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-2">Login Required</h3>
              <p className="text-gray-600 mb-4">
                You need to login first to activate the promo code and get exclusive discounts.
              </p>
              {selectedPromo && (
                <div className="bg-gray-50 rounded-lg p-3 mb-4 text-left">
                  <p className="text-sm font-medium text-gray-900">{selectedPromo.promo_name}</p>
                  <p className="text-xs text-gray-500">Code: {selectedPromo.promo_code}</p>
                  <p className="text-sm text-amber-600 font-medium mt-1">{formatDiscount(selectedPromo)}</p>
                </div>
              )}
              <div className="flex gap-3">
                <button
                  onClick={() => {
                    setShowLoginPrompt(false);
                    setSelectedPromo(null);
                  }}
                  className="flex-1 px-4 py-3 text-gray-700 bg-gray-100 rounded-xl font-medium hover:bg-gray-200 transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={() => {
                    setShowLoginPrompt(false);
                    // Scroll to top and show login
                    window.scrollTo({ top: 0, behavior: 'smooth' });
                    // Trigger login modal - dispatch custom event
                    window.dispatchEvent(new CustomEvent('show-login-modal'));
                  }}
                  className="flex-1 px-4 py-3 bg-gradient-to-r from-amber-400 via-orange-500 to-red-500 text-white rounded-xl font-medium hover:shadow-lg transition-all"
                >
                  Login Now
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
      
      {/* Usage Exceeded Warning Popup */}
      {showUsageExceededPopup && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
          <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full p-6 transform transition-all">
            <div className="text-center">
              <div className="w-16 h-16 mx-auto mb-4 bg-red-100 rounded-full flex items-center justify-center animate-bounce">
                <svg className="w-8 h-8 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.082 16.5c-.77.833.192 2.5 1.732 2.5z" />
                </svg>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-2">Promo Already Used</h3>
              <p className="text-gray-600 mb-4">
                {usageExceededMessage || 'You have reached the maximum usage for this promo code.'}
              </p>
              {selectedPromo && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-3 mb-4 text-left">
                  <div className="flex items-center gap-2 mb-1">
                    <span className="text-xs font-mono font-bold bg-red-200 text-red-700 px-2 py-0.5 rounded">
                      {selectedPromo.promo_code}
                    </span>
                    <span className="text-sm font-medium text-gray-900">{selectedPromo.promo_name}</span>
                  </div>
                  <p className="text-xs text-gray-500">
                    This promo can only be used <strong>{selectedPromo.usage_per_guest || 1}</strong> time{selectedPromo.usage_per_guest > 1 ? 's' : ''} per guest.
                  </p>
                </div>
              )}
              <div className="flex gap-3">
                <button
                  onClick={() => {
                    setShowUsageExceededPopup(false);
                    setSelectedPromo(null);
                    setUsageExceededMessage('');
                  }}
                  className="flex-1 px-4 py-3 bg-gradient-to-r from-gray-500 to-gray-600 text-white rounded-xl font-medium hover:shadow-lg transition-all"
                >
                  OK, I Understand
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PromoBannerSection;
